/**
 * CategoryTreemap - Root cause category distribution
 */

import React, {useCallback, useMemo} from 'react';
import {ResponsiveContainer, Tooltip, Treemap} from 'recharts';
import {Card, CardContent, CardHeader, CardTitle} from '../../../../../ui/card';
import {Button} from '../../../../../ui/button';
import {Skeleton} from '../../../../../ui/skeleton';
import {X} from 'lucide-react';
import {cn} from '../../../../../../lib/utils';
import type {CategoryTreemapProps} from '../../types';
import {TREEMAP_COLORS, TREEMAP_DEFAULT_COLOR,} from '../../constants';

interface TreemapDataItem {
  name: string;
  size: number;
  percentage: number;
  fill: string;
  id: string;
}

/** Props for custom treemap cell content */
interface TreemapContentProps {
  x: number;
  y: number;
  width: number;
  height: number;
  name: string;
  percentage: number;
  fill: string;
  id: string;
}

/**
 * Wrap text into lines that fit a given pixel width.
 * Uses an approximate character budget based on font size and available width.
 * Truncates with ellipsis when lines exceed the available vertical space.
 */
function wrapText(text: string, maxWidth: number, fontSize: number, maxLines: number): string[] {
  const avgCharWidth = fontSize * 0.58;
  const charsPerLine = Math.max(3, Math.floor(maxWidth / avgCharWidth));

  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (test.length <= charsPerLine) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = word.length > charsPerLine ? word.slice(0, charsPerLine - 1) + '\u2026' : word;
    }
    if (lines.length >= maxLines) break;
  }
  if (current && lines.length < maxLines) lines.push(current);

  if (lines.length > maxLines) {
    lines.length = maxLines;
  }
  if (lines.length === maxLines && words.length > 0) {
    const last = lines[maxLines - 1];
    if (last && text.length > lines.join(' ').length) {
      lines[maxLines - 1] = last.length > charsPerLine - 1
        ? last.slice(0, charsPerLine - 1) + '\u2026'
        : last + '\u2026';
    }
  }

  return lines;
}

function CustomContent(props: TreemapContentProps & { selectedCategory?: string; onCategorySelect: (id: string | undefined) => void }) {
  const { x, y, width, height, name, percentage, fill, id, selectedCategory, onCategorySelect } = props;
  const isSelected = selectedCategory === id;

  // Determine label visibility and sizing based on cell dimensions
  const isLargeCell = width > 80 && height > 50;
  const isMediumCell = width > 50 && height > 35;

  // Show label for any cell that can fit text
  const showLabel = width > 30 && height > 25;
  const showPercentage = isMediumCell;

  // Responsive font sizes
  const nameFontSize = isLargeCell ? 12 : isMediumCell ? 10 : 9;
  const percentFontSize = isLargeCell ? 11 : 10;
  const lineHeight = nameFontSize * 1.3;

  // Unique clip path ID for this cell
  const clipId = `clip-${id}-${x}-${y}`;

  // Padding inside cell for text
  const padding = 6;
  const textWidth = Math.max(0, width - padding * 2);

  // Calculate how many name lines we can fit
  const percentLineSpace = showPercentage ? lineHeight + 2 : 0;
  const availableHeight = Math.max(0, height - padding * 2 - percentLineSpace);
  const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

  const nameLines = showLabel ? wrapText(name, textWidth, nameFontSize, maxLines) : [];

  // Vertical centering: total text block height
  const nameBlockHeight = nameLines.length * lineHeight;
  const totalTextHeight = nameBlockHeight + percentLineSpace;
  const textStartY = y + (height - totalTextHeight) / 2 + nameFontSize;

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onCategorySelect(isSelected ? undefined : id);
    }
  }, [onCategorySelect, isSelected, id]);

  return (
    <g
      style={{ cursor: 'pointer' }}
      onClick={() => onCategorySelect(isSelected ? undefined : id)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`${name} category, ${percentage.toFixed(1)}%`}
      className="treemap-cell"
    >
      {/* Define clip path to constrain text within cell bounds */}
      <defs>
        <clipPath id={clipId}>
          <rect
            x={x + padding}
            y={y + padding}
            width={textWidth}
            height={Math.max(0, height - padding * 2)}
          />
        </clipPath>
      </defs>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        stroke={isSelected ? 'hsl(var(--primary))' : 'hsl(var(--border))'}
        strokeWidth={isSelected ? 3 : 1}
        style={{
          opacity: selectedCategory && !isSelected ? 0.56 : 1,
        }}
      />
      {/* Focus ring */}
      <rect
        x={x + 2}
        y={y + 2}
        width={Math.max(0, width - 4)}
        height={Math.max(0, height - 4)}
        fill="none"
        stroke="none"
        strokeWidth={2}
        className="treemap-focus-ring"
        style={{ pointerEvents: 'none' }}
      />
      {showLabel && nameLines.length > 0 && (
        <g clipPath={`url(#${clipId})`} style={{ pointerEvents: 'none' }}>
          {nameLines.map((line, i) => (
            <text
              key={i}
              x={x + width / 2}
              y={textStartY + i * lineHeight}
              textAnchor="middle"
              fill="#fff"
              fontSize={nameFontSize}
              fontWeight={400}
            >
              {line}
            </text>
          ))}
          {showPercentage && (
            <text
              x={x + width / 2}
              y={textStartY + nameLines.length * lineHeight + 2}
              textAnchor="middle"
              fill="#fff"
              fontSize={percentFontSize}
              opacity={0.9}
            >
              {percentage.toFixed(1)}%
            </text>
          )}
        </g>
      )}
    </g>
  );
}

export function CategoryTreemap({
  data,
  selectedCategory,
  onCategorySelect,
  isLoading,
  isError,
  className,
}: CategoryTreemapProps) {
  const treemapData = useMemo(() => {
    if (!data?.categories) return [];

    return data.categories.map((category, index): TreemapDataItem => ({
      name: category.name,
      size: category.count,
      percentage: category.percentage,
      fill: TREEMAP_COLORS[index % TREEMAP_COLORS.length] || TREEMAP_DEFAULT_COLOR,
      id: category.id,
    }));
  }, [data]);

  if (isLoading) {
    return (
      <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)} aria-busy="true">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)}>
        <CardHeader>
          <CardTitle>Root Cause Categories</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-destructive">
            Failed to load category data
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || treemapData.length === 0) {
    return (
      <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)}>
        <CardHeader>
          <CardTitle>Root Cause Categories</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-muted-foreground">
            No root cause data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)} data-testid="category-treemap">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <CardTitle>Root Cause Categories</CardTitle>
        {selectedCategory && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onCategorySelect(undefined)}
            className="text-muted-foreground"
          >
            <X className="h-4 w-4 mr-1" />
            Clear filter
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <style>{`.treemap-cell:focus { outline: none; } .treemap-cell:focus-visible .treemap-focus-ring { stroke: hsl(var(--ring)); }`}</style>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={treemapData}
              dataKey="size"
              stroke="hsl(var(--background))"
              content={<CustomContent x={0} y={0} width={0} height={0} name="" percentage={0} fill="" id="" selectedCategory={selectedCategory} onCategorySelect={onCategorySelect} />}
            >
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null;
                  const item = payload[0].payload as TreemapDataItem;
                  return (
                    <div className="rounded-2xl border border-border/70 bg-popover/95 p-3 shadow-[var(--shadow-card)] backdrop-blur-sm">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.size.toLocaleString()} records ({item.percentage.toFixed(1)}%)
                      </p>
                    </div>
                  );
                }}
              />
            </Treemap>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-3" role="group" aria-label="Category filter">
          {treemapData.map((item) => (
            <div
              key={item.id}
              className={cn(
                'flex items-center gap-2 rounded-full border border-transparent px-2 py-1 text-sm cursor-pointer transition-colors hover:border-primary/15 hover:bg-accent/50',
                selectedCategory === item.id && 'font-medium'
              )}
              role="button"
              aria-pressed={selectedCategory === item.id}
              tabIndex={0}
              onClick={() => onCategorySelect(
                selectedCategory === item.id ? undefined : item.id
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onCategorySelect(selectedCategory === item.id ? undefined : item.id);
                }
              }}
            >
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: item.fill }}
              />
              <span>{item.name}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
